/**
 * Las diez medidas de Jesús. Las suyas, todas, siempre.
 *
 * "Las medidas se tienen que pedir las que he pedido siempre. Sirvan o no, se piden
 * siempre" (06-08-2026). Antes se pedían cinco, y solo la cintura era obligatoria: el
 * resto iba plegado detrás de un "añadir el resto (opcional)".
 *
 * POR QUÉ IMPORTA QUE ESTÉN TODAS Y SIEMPRE: el motivo de que no se fíe de las medidas es
 * que cada mes se mide de una forma distinta. Con el vídeo y la instrucción delante, el
 * error al menos se repite, y lo que interesa no es que el número sea exacto sino que se
 * pueda comparar con el del mes pasado.
 */

export const MEDIDAS = [
    { key: 'hombros', label: 'Hombros' },
    { key: 'mesoesternal', label: 'Mesoesternal' },
    { key: 'brazo_d', label: 'Brazo derecho relajado' },
    { key: 'brazo_i', label: 'Brazo izquierdo relajado' },
    { key: 'muslo_d', label: 'Muslo derecho' },
    { key: 'muslo_i', label: 'Muslo izquierdo' },
    { key: 'cadera', label: 'Cadera' },
    { key: 'cintura', label: 'Cintura' },
    { key: 'gemelo_d', label: 'Gemelo derecho' },
    { key: 'gemelo_i', label: 'Gemelo izquierdo' },
];

/**
 * Las que se guardaron con los nombres viejos, para que el "mes pasado" no salga vacío
 * en el primer reporte con las diez.
 *
 * Solo se traducen las que son LA MISMA medida: la cintura y la cadera. El "pecho" viejo
 * NO se da por mesoesternal, ni el "brazo" por el derecho, porque no se sabe cómo se
 * tomaron y compararlos daría saltos que no ha dado el cliente. En esas, el mes pasado
 * aparece en blanco hasta que haya un dato nuevo, que es lo honesto.
 */
const EQUIVALENTES = { cintura: 'waist', cadera: 'hip' };

// El valor del mes pasado para una medida, mirando también el nombre viejo.
export const valorAnterior = (medidas, key) => {
    if (!medidas) return null;
    const v = medidas[key];
    if (v != null && v !== '') return Number(v);
    const viejo = EQUIVALENTES[key];
    if (viejo && medidas[viejo] != null && medidas[viejo] !== '') return Number(medidas[viejo]);
    return null;
};

// La diferencia con el mes pasado, ya formateada: "+1,5" / "-0,5" / null si no procede.
export const diferencia = (ahora, antes) => {
    const a = parseFloat(ahora);
    if (isNaN(a) || antes == null) return null;
    const d = Math.round((a - antes) * 10) / 10;
    if (d === 0) return { texto: 'igual', signo: 0 };
    return { texto: `${d > 0 ? '+' : ''}${String(d).replace('.', ',')}`, signo: Math.sign(d) };
};

// LAS MEDIDAS DE UN REPORTE, CON SU NOMBRE: las diez de ahora (leídas también por el nombre
// viejo) y, si `antiguas`, lo que quede fuera de las diez (pecho, brazo y muslo de los
// reportes de antes) con su nombre: son medidas que el cliente mandó, y descartarlas en
// silencio es peor que enseñarlas etiquetadas. Antes vivía dentro del bloque de Macros.
const NOMBRES_VIEJOS = { chest: 'Pecho (medida antigua)', arm: 'Brazo (medida antigua)', thigh: 'Muslo (medida antigua)' };
export const filasDeMedidas = (medidas, { antiguas = true } = {}) => {
    if (!medidas) return [];
    const deLasDiez = MEDIDAS
        .map(({ key, label }) => ({ key, label, valor: valorAnterior(medidas, key) }))
        .filter(m => m.valor != null);
    if (!antiguas) return deLasDiez;
    const conocidas = new Set([...MEDIDAS.map(m => m.key), ...Object.values(EQUIVALENTES)]);
    return [...deLasDiez, ...Object.entries(medidas)
        .filter(([k, v]) => k !== 'fecha' && !conocidas.has(k) && v !== '' && v != null && Number.isFinite(Number(v)))
        .map(([k, v]) => ({ key: k, label: NOMBRES_VIEJOS[k] || k, valor: Number(v) }))];
};

// LAS MEDIDAS DE UN FORMULARIO DE CALMA (20-10). Calma las guardaba como una lista de diez
// números («121|108|38|...», `calma_raw...mediciones.valores`) en el mismo orden que las diez
// de Jesús, y la ficha las pintaba «121 · 108 · 38...» sin decir cuál era cuál. Aquí pasan
// a {clave: cm} para `filasDeMedidas`. Un 0 o un hueco no es una medida, y una serie con
// todos los valores iguales («1|1|1...») es el relleno del formulario, no diez medidas: la
// misma regla que la Evolución (`decodificar_medidas`) y el cuestionario de la ficha.
export const medidasDeCalma = (valores) => {
    if (!Array.isArray(valores)) return null;
    const presentes = valores.filter(v => v != null && v !== '' && Number(v) > 0).map(Number);
    if (presentes.length > 1 && presentes.every(v => v === presentes[0])) return null;
    const medidas = {};
    MEDIDAS.forEach(({ key }, i) => {
        const v = Number(valores[i]);
        if (valores[i] != null && Number.isFinite(v) && v > 0) medidas[key] = v;
    });
    return Object.keys(medidas).length ? medidas : null;
};

// LA TOMA DE UN REPORTE Y LA DE ANTES, de la lista de tomas del servidor (`tomas_medidas`
// de GET .../puntos, `core/puntos._tomas_de`): ahí ya están las tres puertas (reportes,
// medidas apuntadas sueltas y las del alta), limpias y en orden. «Una toma es una toma,
// venga de donde venga» (Jesús): la de antes puede ser la del alta. Aquí solo se busca.
export const tomaDelReporteYAnterior = (tomas, reporteId) => {
    const lista = (tomas || []).filter(t => t?.id && t?.fecha && t.measurements);
    const i = lista.findIndex(t => t.id === reporteId);
    return i < 0 ? { toma: null, anterior: null } : { toma: lista[i], anterior: lista[i - 1] || null };
};

// Cómo medir los perímetros. Va incrustado arriba del bloque y siempre visible: es lo
// único que hace que el error se repita igual todos los meses.
export const VIDEO_MEDIDAS = 'https://player.vimeo.com/video/796836155?h=e1436af05e';

/**
 * QUÉ ES UNA MEDIDA CREÍBLE (12-10). Son contornos en centímetros y no tenían ni suelo ni
 * techo: "hombros 9999" pasaba al resumen, se enviaba y quedaba en la ficha; y con un 0 la
 * pantalla decía "puestas" mientras el servidor contestaba que faltaba una. El rango es
 * ancho a propósito: solo caza la errata. Es la misma regla que valida el servidor
 * (`backend/models/common.py`, `medida_creible`), para que no haya una puerta más blanda
 * que la otra.
 */
export const MEDIDA_MIN_CM = 10;
export const MEDIDA_MAX_CM = 250;

const numero = (v) => Number(String(v ?? '').replace(',', '.'));

/**
 * Las medidas, revisadas. No pinta nada: devuelve qué pasa y con qué frase.
 *
 * En el reporte van las diez y todas tienen que ser medidas. Con `todas: false` (el alta y
 * "Añadir medidas" de Seguimiento, donde puede dejar alguna en blanco) no se exige ninguna,
 * pero la que esté escrita pasa por el mismo rango: el servidor descarta callado la que
 * está fuera, y el cliente creía haberla guardado.
 * @returns {{ok: boolean, error?: string}}
 */
export function revisarMedidas(valores, { todas = true } = {}) {
    const v = valores || {};
    const escrita = (m) => v[m.key] != null && String(v[m.key]).trim() !== '';
    if (todas) {
        // FALTA la que está en blanco. Un 0 o un -10 no faltan: están mal escritos, y eso lo
        // dice la frase del rango de abajo («Te falta una medida: hombros» con un 0 puesto
        // mandaba a buscar una casilla vacía que no había, 14-10).
        const faltan = MEDIDAS.filter(m => !escrita(m));
        if (faltan.length) {
            const primera = faltan[0].label.toLowerCase();
            return {
                ok: false,
                error: faltan.length === 1
                    ? `Te falta una medida: ${primera}`
                    : `Te faltan ${faltan.length} medidas, y van todas: empieza por ${primera}`,
            };
        }
    }
    const rara = MEDIDAS.find(m => (todas || escrita(m))
        && !(numero(v[m.key]) >= MEDIDA_MIN_CM && numero(v[m.key]) <= MEDIDA_MAX_CM));
    if (rara) {
        return {
            ok: false,
            error: `Revisa la medida de ${rara.label.toLowerCase()}: van en centímetros, `
                + `entre ${MEDIDA_MIN_CM} y ${MEDIDA_MAX_CM}.`,
        };
    }
    return { ok: true };
}
